/**
 * Raid Logic Core - Pure Functions
 *
 * Extract pure game logic functions from raid.js without browser dependencies
 *
 * These are exact copies of functions from raid.js that:
 * - Calculate drop rates and loot pools
 * - Generate raid grid layouts
 * - Determine faction-based drops
 * - Validate paths and calculate game mechanics
 *
 * Can be imported and used in Node.js for testing and simulation
 */

import * as config from '../constants.js';
import { getAllItems, shuffleArray, getEffect } from './util.js';
import { generateRoomNames } from '../data/raidRegions.js';
import { gameState, ensureLifetimeCounters } from './gameState-core.js'
import { multiplayer, recordContractorPayments } from './multiplayer.js'
import { generateRoadNetwork, applyRoadNetwork, MAJOR } from './roadNetwork-core.js'
import { initializeRoomEvents, processScheduledEventsCore, cleanupRoomEvents, countRoomEvents, presentBonusLootEvent, presentFishingEvent, logEventProblem, processSkillCheckEvent, getItemPoolForRarity, preRollGridEvents } from './eventSystem-core.js'
import { addNotification } from './notifications.js'
import { gainExperienceCore, gainContractorExperienceCore, getSkillArtifactBonus, getUtilityArtifactBonus } from './character-core.js';
import { getRarityDropChance, getRarityQuantityRange, addLoot, addLogEntry, addRaidMessage,
  getRoomFaction, getBlueprintsForRoom, getFactionSpecificMaterials, getItemsByType, regionTierToRarity,
  applyCreditsScaling
}  from './raidUtil.js'
import { debug, isDebug, CH } from './debugLog.js';
import { noteRaidCompletedForSuccession } from './syndicate-core.js';
import { blackoutModifier, clearActiveBlackout, getBlackoutContract } from './blackout-core.js';
import { rollCollapseKeyCore } from './collapseKeys-core.js';

// ============================================================================
// GRID GENERATION & LAYOUT
// ============================================================================

/**
 * Helper function to add dynamic room names
 * Exact copy from raid.js line 104 (not exported)
 */
export function addRoomNames(room, regionKey, zone, row, col) {
  if (!room || !room.name || !room.category) return room;

  const roomNames = generateRoomNames({
    baseName: room.name,
    regionKey,
    zone,
    category: room.category,
    seed: `${regionKey}:${row},${col}`
  });

  return {
    ...room,
    nameOptions: roomNames,
    displayName: roomNames[0] // Default to first option
  };
}

/**
 * Pick a room template by category, difficulty, and the district it will stand
 * in. Used for the rooms the map PLACES — the checkpoints and the high-value
 * targets — not for the fill, which has its own selection.
 *
 * @param {string} [zone] the DISTRICT of the cell being filled. Matched against
 *   a template's `districtAffinity`, which is the template's own opinion about
 *   where it belongs. Both were called `zone` until recently, which is most of
 *   why nobody could tell what the property did.
 */
export function selectRoomByCategory(rooms, categories, difficulty = null, zone = null) {
  // Normalize categories to array
  if (!Array.isArray(categories)) categories = [categories];

  // Filter by category
  let filtered = rooms.filter(r => categories.includes(r.category));

  // Further filter by difficulty if specified
  if (difficulty && filtered.length > 0) {
    const difficultyFiltered = filtered.filter(r => r.difficulty === difficulty);
    if (difficultyFiltered.length > 0) filtered = difficultyFiltered;
  }

  // Then by district. A template that names one is asking to be kept for it;
  // a template that names none will stand anywhere, so it stays in the running
  // either way. This is the one place a template's own district preference has
  // ever had an effect, and it is worth keeping: it is what puts an apartment
  // gate in the housing and an arcade gate on the shopping street.
  if (zone && filtered.length > 0) {
    const local = filtered.filter(r => r.districtAffinity === zone || !r.districtAffinity);
    if (local.length > 0) filtered = local;
  }

  // Fallback to all rooms if no matches
  if (filtered.length === 0) filtered = rooms;

  return filtered[Math.floor(Math.random() * filtered.length)];
}


/**
 * Create a road room object
 * Exact copy from raid.js line 422
 */
const createRoad = (row, col) => ({ ...config.commonAreas['road'], row: row, col: col});

/**
 * Create a campsite room object
 * Exact copy from raid.js line 420
 */
const createCampsite = (row, col) => ({ ...config.commonAreas ['campsite'], row: row, col: col});

/**
 * Create a water room object
 * Exact copy from raid.js line 421
 */
const createWater = (row, col) => ({ ...config.commonAreas['water'], row: row, col: col});

/**
 * Generate the raid grid for a given region and tier
 * Exact copy from raid.js line 123
 */
export function generateRaidGrid(regionKey, tier) {
  const region = config.raidRegions[regionKey];
  const tierInfo = config.raidTierData[tier];
  const gridRows = tierInfo.gridRows || tierInfo.gridSize;
  const gridCols = tierInfo.gridCols || tierInfo.gridSize;

  // Generate strategic layout for all regions
  return generateStrategicRaid(region, gridRows, gridCols, tier, regionKey);
}

/**
 * Generate strategic raid layout with zone-based room placement
 * Exact copy from raid.js line 134
 */
export function generateStrategicRaid(region, gridRows, gridCols, tier, regionKey) {
  const grid = [];

  // Initialize empty grid
  for (let row = 0; row < gridRows; row++) {
    grid[row] = [];
    for (let col = 0; col < gridCols; col++) {
      grid[row][col] = null;
    }
  }

  // The road layout is decided before anything is placed, so the fast-travel
  // rooms can be put ON the trunk rather than scattered independently of it.
  const startRowSeed = Math.floor(gridRows / 2);
  const roadNet = generateRoadNetwork(gridRows, gridCols, Math.random, {
    // the trunk runs edge to edge and so do these, so keep it off them
    avoid: new Set([`${startRowSeed},0`, `${startRowSeed},${gridCols - 1}`]),
  });

  // Define zones for strategic placement
  const zones = generateDistricts(gridRows, gridCols);

  // Set start and end positions
  const startRow = Math.floor(gridRows / 2);
  const endRow = Math.floor(gridRows / 2);

  grid[startRow][0] = {
    type: 'start',
    name: 'Entry',
    primary: '',
    support: [],
    row: startRow,
    col: 0,
    zone: 'entry',
    baseDuration: 0,
    icon: '🌀'
  };

  grid[endRow][gridCols - 1] = {
    type: 'exit',
    name: 'Exit',
    primary: '',
    support: [],
    row: endRow,
    col: gridCols - 1,
    zone: 'core',
    baseDuration: 0,
    icon: '🌌'
  };

  // Generate strategic layout
  generateStrategicInfrastructure(grid, zones, gridRows, gridCols, roadNet);
  placeSecurityCheckpoints(grid, zones, region, regionKey, gridRows, gridCols);
  placeHighValueTargets(grid, zones, region, regionKey);
  addAlternateRoutes(grid, zones, region, regionKey, gridRows, gridCols);

  // Fill any remaining null cells with road rooms
  fillEmptyCells(grid, gridRows, gridCols);

  // Place campsite after grid is fully populated
  placeCampsite(grid, gridRows, gridCols,
    new Set([...roadNet.values()].filter(c => c.rank === MAJOR).map(c => `${c.row},${c.col}`)));

  // Stamp the road layer onto whatever ended up in each cell. It is a layer,
  // not a room type: a gate the trunk runs past shows its frontage, and a tile
  // the network misses draws no road at all.
  // campsite and water may end a track; collapsed ground may not even be
  // approached, so it drops out of the network entirely
  applyRoadNetwork(grid, gridRows, gridCols, roadNet, ['campsite', 'water'], ['blocked']);

  markClearings(grid, gridRows, gridCols);

  // Every cell learns its district, including the ones placed by hand and the
  // ones that are not encounters at all. Placement stamps the district on the
  // rooms it selects, but the roads, the water, the campsite and the collapsed
  // ground never went through it — and the renderer wants a district on those
  // as much as on anything else, since growth reclaiming the outskirts should
  // not stop at a road tile's border.
  applyZones(grid, zones);

  // ...and, on a region the sea got into, how far each cell is from it. This is
  // stamped LAST and read only by the renderer: nothing above this line has seen
  // it, so the map that gets played is the same map either way.
  const shore = hasShoreline(regionKey) ? pickShore() : null;
  if (shore) applyShore(grid, shore);
  const railway = hasRailway(regionKey) ? pickRailway(grid, gridCols) : null;
  if (railway != null) applyRailway(grid, railway);

  // Commit each encounter room to the events it will run. The map is drawn
  // from this, so it has to be decided before the grid is displayed — and it
  // is safe here because the event pool depends only on the room's own skills,
  // not on the team, which is not hired until after planning.
  preRollGridEvents(grid);

  return {
    grid: grid,
    rows: gridRows,
    cols: gridCols,
    startPos: { row: startRow, col: 0 },
    endPos: { row: endRow, col: gridCols - 1 },
    region: region,
    tier: tier,
    zones: zones,
    shore: shore,
    railway: railway
  };
}

// ---- districts ----
// A raid is a walk from the west edge to the east one, so a district is a BAND
// of columns: you leave the entry, cross two or three neighbourhoods, and
// arrive at the core. What the map used to do instead was carve fixed
// rectangles out of the grid dimensions — which meant every raid of a tier was
// laid out identically, `commercial` swallowed `security` whole at 7 columns
// because the rectangles overlapped and the first match won, and about a third
// of a 17-column map matched no rectangle at all and fell through to
// `peripheral` by default rather than by design.

const ANCHOR_COLS = 2;   // columns the entry and core districts hold at the ends
const MIN_BAND = 3;      // narrower than this and a district cannot be read as one
const SPLIT_CHANCE = 0.4; // how often a band is cut north/south into two districts
const MIN_SPLIT = 2;      // rows each half of a cut band must have to read as one

/**
 * Lay a map out in districts.
 *
 * Entry and core are pinned to the west and east edges: they are what the
 * player navigates by, and the run has to mean something. Everything between
 * them is dealt from the archetype pool in a shuffled order, so two raids of
 * the same tier cross different neighbourhoods in a different sequence.
 *
 * Two things stop it reading as a bar chart. Boundaries wobble a column either
 * way per row, so districts interlock instead of butting up in straight lines.
 * And a band may be cut north/south and its top half given to another
 * archetype — without that, at seven rows every district spans the full height
 * of the map and the whole thing reads as vertical stripes.
 *
 * @param {function} [rand] injectable so a test can pin an exact layout; the
 *   map itself is generated once and persisted, so it has no need of a seed
 * @returns {{cells: string[][], order: string[]}} every cell's district, and
 *   the west-to-east sequence it was dealt from
 */
export function generateDistricts(gridRows, gridCols, rand = Math.random) {
  const middle = Object.keys(config.zoneArchetypes).filter(z => z !== 'entry' && z !== 'core');
  const free = Math.max(0, gridCols - ANCHOR_COLS * 2);
  const bands = Math.max(1, Math.min(middle.length, Math.floor(free / MIN_BAND)));
  const picked = shuffleArray(middle, rand);
  const order = ['entry', ...picked.slice(0, bands), 'core'];

  // Where each district starts, before the per-row wobble. The middle bands
  // split what the anchors leave as evenly as the columns allow.
  const starts = [0];
  for (let i = 0; i <= bands; i++) starts.push(ANCHOR_COLS + Math.round((free * i) / bands));
  starts.push(gridCols);

  // A band may hand half of itself to an archetype the order did not use.
  // Which half is rolled too: always giving away the north would put every
  // split district along the top of the map, which is its own fixed pattern.
  const spare = picked.slice(bands);
  const splits = order.map((_, i) => {
    if (i === 0 || i === order.length - 1 || !spare.length) return null;
    if (gridRows < MIN_SPLIT * 2 || rand() >= SPLIT_CHANCE) return null;
    // Both halves get MIN_SPLIT rows at least: a one-row strip of another
    // district does not read as a district, it reads as a stray row.
    const room = gridRows - MIN_SPLIT * 2;
    return { at: MIN_SPLIT + Math.floor(rand() * (room + 1)), north: rand() < 0.5, zone: spare.pop() };
  });

  const cells = [];
  for (let row = 0; row < gridRows; row++) {
    // Wobble every interior boundary, left to right, each one kept at least a
    // column past the last and far enough from the east edge that every
    // district still left to place gets a column of its own. Districts can
    // therefore never cross or vanish, whatever the rolls do.
    const edge = [0];
    for (let i = 1; i < starts.length - 1; i++) {
      const drift = rand() < 0.5 ? 0 : (rand() < 0.5 ? -1 : 1);
      edge.push(Math.max(edge[i - 1] + 1,
        Math.min(starts[i] + drift, gridCols - (order.length - i))));
    }
    edge.push(gridCols);

    const line = [];
    for (let i = 0; i < order.length; i++) {
      const cut = splits[i];
      const zone = cut && (row < cut.at) === cut.north ? cut.zone : order[i];
      for (let col = edge[i]; col < edge[i + 1]; col++) line[col] = zone;
    }
    cells.push(line);
  }

  return { cells, order };
}

/**
 * Stamp every cell with the district it sits in — roads, water, campsite,
 * blocked ground and the portals included. One rule and no exceptions, so
 * `room.zone` means the same thing wherever it is read from.
 */
export function applyZones(grid, zones) {
  for (let row = 0; row < grid.length; row++) {
    for (let col = 0; col < grid[row].length; col++) {
      if (grid[row][col]) grid[row][col].zone = determineZone(row, col, zones);
    }
  }
}

// ---- the shoreline ----
// A drowned region needs somewhere for the water to have come FROM. Scattering
// standing water evenly over every tile is what the flood overlay used to do,
// and it read as polka dots: the map was uniformly damp instead of being a place
// the sea had got into from one side.
//
// So one edge of the map is open ocean. Which edge is rolled per raid, north or
// south, and it is safe either way by construction: districts are west-to-east
// column BANDS and the entry and extraction portals both sit in the middle row,
// so a horizontal edge can never take a district's anchor or a portal.
//
// The sea is a RENDERING fact and nothing more. It sets no room's type, blocks
// nothing, and changes no path — the water reaches about halfway into the shore
// row's tiles, so that row stays as walkable as any other and the map's balance
// is exactly what it was. That constraint is the whole reason it is expressed as
// a distance rather than as a row of water tiles.

/** @returns {'n'|'s'} which edge of the map is open water */
export function pickShore(rand = Math.random) {
  return rand() < 0.5 ? 'n' : 's';
}

/**
 * Stamp every cell with how far it is from the ocean edge, in whole tiles, and
 * which edge that is. Same "everything, no exceptions" rule as applyZones: the
 * renderer wants the shoreline on a road tile as much as on a room.
 *
 * The shore row's seaward side is also closed to the roadway. The sea wall runs
 * across those tiles and is drawn as terrain — effectively where the map stops —
 * so a carriageway heading that way would run at a concrete face and end. The
 * network is only ever losing a stub here: nothing beyond the wall was reachable
 * to begin with, so no route through the map is affected.
 */
export function applyShore(grid, side) {
  const rows = grid.length;
  const seaward = side === 'n' ? 'n' : 's';
  for (let row = 0; row < rows; row++) {
    const shore = side === 'n' ? row : rows - 1 - row;
    for (let col = 0; col < grid[row].length; col++) {
      const room = grid[row][col];
      if (!room) continue;
      room.shore = shore;
      room.shoreSide = side;
      if (shore === 0 && room.road) delete room.road[seaward];
    }
  }
}

/** Regions whose art direction says the sea got in. */
const SHORELINE_REGIONS = new Set(['sunken-bastions']);

export const hasShoreline = regionKey => SHORELINE_REGIONS.has(regionKey);

// ---- the railway ----
// A sublevel is transit infrastructure before it is anything else, so it gets a
// rail spur — but ONE, and laid the way a road is laid rather than scattered.
// Sprinkling rail beds around as props gave the map disconnected lengths of
// track that started and stopped for no reason; a railway is only a railway if
// it goes somewhere. The trunk road runs west to east, so the line runs north
// to south and the two cross, which is what a transit interchange looks like.

/** Regions whose art direction says something still runs on rails down there. */
const RAILWAY_REGIONS = new Set(['hollow-core']);

export const hasRailway = regionKey => RAILWAY_REGIONS.has(regionKey);

/** Ground the line may not be laid across at all. */
const RAIL_BLOCKERS = new Set(['water', 'campsite']);

/**
 * The column the line runs down, or null if the map has nowhere to put one.
 *
 * Two different things are being avoided here and they are not the same
 * strength, which is the whole subtlety:
 *
 *  - A lake or a rest stop is ground the line cannot cross, so a column holding
 *    one is rejected outright. Every map has at least one column that is clear.
 *  - A ROAD cell is different. Track and road crossing each other is a level
 *    crossing and perfectly ordinary; what looked wrong was track running ALONG
 *    the network, which made the line read as a third carriageway joining two
 *    others rather than as a railway. So road cells are a cost to minimise, not
 *    a veto — and in practice the best column has either none or one.
 *
 * @returns {number|null}
 */
export function pickRailway(grid, gridCols, rand = Math.random) {
  // Kept off the entry and extraction columns too: those are the portals' own,
  // and a line down either would run through the room the raid starts or ends in.
  let best = Infinity, options = [];
  for (let col = 2; col <= gridCols - 3; col++) {
    let blocked = false, roads = 0;
    for (let row = 0; row < grid.length && !blocked; row++) {
      const room = grid[row][col];
      if (!room || RAIL_BLOCKERS.has(room.type)) blocked = true;
      else if (room.type === 'road') roads++;
    }
    if (blocked) continue;
    if (roads < best) { best = roads; options = [col]; }
    else if (roads === best) options.push(col);
  }
  return options.length ? options[Math.floor(rand() * options.length)] : null;
}

/** Stamp the line onto its column. */
export function applyRailway(grid, col) {
  for (let row = 0; row < grid.length; row++) {
    if (grid[row][col]) grid[row][col].rail = true;
  }
}

/**
 * Generate strategic infrastructure and blocked areas
 * Exact copy from raid.js line 228
 */
export function generateStrategicInfrastructure(grid, zones, gridRows, gridCols, roadNet) {
  // First, create road network
  createRoadNetwork(grid, zones, gridRows, gridCols, roadNet);

  // Create blocked areas representing collapsed structures (randomized positions)
  //
  // Roll each one clear of the road network where possible. Collapsed ground
  // carries no pathway at all, and a track cut short by rubble has its whole
  // spur pruned back to the last junction — so rubble dropped on the network
  // costs several tiles of reachable map, not one. Falls back to the first roll
  // when the grid is too congested to find anything better.
  const offNetwork = spot => {
    for (let tries = 0; tries < 12; tries++) {
      const candidate = spot();
      if (!roadNet?.has(`${candidate.row},${candidate.col}`)) return candidate;
    }
    return spot();
  };
  const blockedAreas = [
    // Collapsed skybridge (randomized position)
    offNetwork(() => ({ row: 1 + Math.floor(Math.random() * Math.max(1, gridRows-4)), col: Math.floor(gridCols * (0.3 + Math.random() * 0.4)), type: 'collapsed_bridge' })),
    // Radiation zone in industrial area (randomized)
    offNetwork(() => ({ row: Math.floor(gridRows * (0.6 + Math.random() * 0.3)), col: 2 + Math.floor(Math.random() * 3), type: 'radiation_zone' })),
    // Structural damage in commercial district (randomized)
    offNetwork(() => ({ row: 1 + Math.floor(Math.random() * 3), col: 1 + Math.floor(Math.random() * 3), type: 'structural_damage' })),
  ];

  blockedAreas.forEach(blocked => {
    if (blocked.row >= 0 && blocked.row < gridRows && blocked.col >= 0 && blocked.col < gridCols) {
      if (!grid[blocked.row][blocked.col]) {
        grid[blocked.row][blocked.col] = {
          type: 'blocked',
          name: 'Blocked',
          row: blocked.row,
          col: blocked.col,
          subtype: blocked.type
        };
      }
    }
  });
}

/**
 * Create sparse, strategic road network throughout the city
 * Exact copy from raid.js line 258
 */
export function createRoadNetwork(grid, zones, gridRows, gridCols, roadNet) {
  // Fast-travel rooms sit ON the trunk. They used to be scattered by their own
  // rules, which had two problems: the "main thoroughfare" was a straight run
  // down the centre row covering only part of the width, and the vertical
  // connector wrote grid[centerRow][col] inside a loop over ROW, so it only
  // ever placed a single cell. Putting them on the trunk also means the road
  // you can see and the road you can travel quickly are the same road.
  const net = roadNet || generateRoadNetwork(gridRows, gridCols);
  const trunk = [...net.values()].filter(c => c.rank === MAJOR);

  // EVERY trunk cell becomes a road room. Budgeting them (as an earlier pass
  // did) left most of the route as gates and hazards, which then drew the
  // roadway across their own frontage — a four-lane road through the middle of
  // a warehouse. The trunk you can see and the trunk you can travel are the
  // same cells, so the roadway only ever renders on a road tile.
  //
  // This runs before blocked areas and every encounter placement, and those
  // only ever fill EMPTY cells, so nothing can punch a hole in the route
  // afterwards. The one thing already on the grid is the start/exit pair.
  for (const cell of trunk) {
    if (grid[cell.row]?.[cell.col]) continue;   // never displace start/exit
    grid[cell.row][cell.col] = createRoad(cell.row, cell.col);
  }
}

/**
 * Place security checkpoints as strategic chokepoints
 * Exact copy from raid.js line 317
 */
export function placeSecurityCheckpoints(grid, zones, region, regionKey, gridRows, gridCols) {
  // Main security gate controlling access to core
  const coreGateRow = Math.floor(grid.length / 2);
  const coreGateCol = grid[0].length - 3;

  if (!grid[coreGateRow][coreGateCol]) {
    const currentZone = determineZone(coreGateRow, coreGateCol, zones);
    const gateTemplate = selectRoomByCategory(region.gates, 'security', 'chokepoint', currentZone);
    const roomWithNames = addRoomNames(gateTemplate, regionKey, currentZone, coreGateRow, coreGateCol);
    grid[coreGateRow][coreGateCol] = {
      type: 'gate',
      ...roomWithNames,
      priority: 'critical',
      zone: currentZone,
      strategic_value: 'chokepoint',
      row: coreGateRow,
      col: coreGateCol
    };
  }

  // Secondary checkpoints with zone-aware placement
  const secondaryGates = [
    { row: 2, col: Math.floor(grid[0].length / 3) },
    { row: grid.length - 3, col: Math.floor(grid[0].length / 3) },
    { row: 1, col: 4 }, // Residential gate
    { row: grid.length - 2, col: 4 }, // Residential gate
    { row: 3, col: 3 }, // Commercial gate
    { row: grid.length - 4, col: 3 } // Commercial gate
  ];

  secondaryGates.forEach(pos => {
    if (pos.row >= 0 && pos.row < grid.length && pos.col >= 0 && pos.col < grid[0].length) {
      if (!grid[pos.row][pos.col]) {
        const currentZone = determineZone(pos.row, pos.col, zones);
        const gateTemplate = selectRoomByCategory(region.gates, 'security', 'chokepoint', currentZone);
        const roomWithNames = addRoomNames(gateTemplate, regionKey, currentZone, pos.row, pos.col);
        grid[pos.row][pos.col] = {
          type: 'gate',
          ...roomWithNames,
          priority: 'high',
          zone: currentZone,
          strategic_value: 'access_control',
          row: pos.row,
          col: pos.col
        };
      }
    }
  });
}

/**
 * Place high-value targets in protected areas
 * Exact copy from raid.js line 368
 */
export function placeHighValueTargets(grid, zones, region, regionKey, gridRows, gridCols) {
  // Primary target: High-value storage in core zone
  const vaultRow = Math.floor(grid.length / 2);
  const vaultCol = grid[0].length - 2;

  if (!grid[vaultRow][vaultCol]) {
    const currentZone = determineZone(vaultRow, vaultCol, zones);
    const hazardTemplate = selectRoomByCategory(region.hazards, 'storage', 'high_value', currentZone);
    const roomWithNames = addRoomNames(hazardTemplate, regionKey, currentZone, vaultRow, vaultCol);
    grid[vaultRow][vaultCol] = {
      type: 'hazard',
      ...roomWithNames,
      priority: 'critical',
      zone: currentZone,
      strategic_value: 'high_value_target',
      reward_multiplier: 2.5,
      row: vaultRow,
      col: vaultCol
    };
  }

  // Secondary targets spread throughout with zone-specific placement
  const secondaryTargets = [
    { row: 1, col: grid[0].length - 4, category: ['power', 'infrastructure'] },
    { row: grid.length - 2, col: grid[0].length - 4, category: ['infrastructure', 'storage'] },
    { row: 1, col: 5, category: ['environmental', 'infrastructure'] }, // Residential
    { row: grid.length - 2, col: 5, category: ['environmental', 'storage'] }, // Residential
    { row: 3, col: 4, category: ['storage', 'infrastructure'] }, // Commercial
    { row: grid.length - 4, col: 4, category: ['storage', 'power'] } // Commercial
  ];

  secondaryTargets.forEach(target => {
    if (target.row >= 0 && target.row < grid.length && target.col >= 0 && target.col < grid[0].length) {
      if (!grid[target.row][target.col]) {
        const currentZone = determineZone(target.row, target.col, zones);
        const hazardTemplate = selectRoomByCategory(region.hazards, target.category, 'high_value', currentZone);
        const roomWithNames = addRoomNames(hazardTemplate, regionKey, currentZone, target.row, target.col);
        grid[target.row][target.col] = {
          type: 'hazard',
          ...roomWithNames,
          priority: 'high',
          zone: currentZone,
          strategic_value: 'infrastructure',
          reward_multiplier: 1.5,
          row: target.row,
          col: target.col
        };
      }
    }
  });
}

/** The rooms nobody paved: their ground is turf, not the region's floor. */
const CLEARING_TYPES = new Set(['campsite', 'water']);

/**
 * Tell each clearing which of its sides the open ground carries on across.
 *
 * The renderer thins its turf out to nothing at the tile edge so a clearing
 * meets a paved neighbour without a line around it — but the rest stop and its
 * shoreline are placed side by side by construction, so both were fading out at
 * the edge they share and leaving a strip of tarmac down the middle of one
 * clearing. Same shape of problem as the road layer, and the same answer: work
 * it out once, here, where the whole grid is in hand, and stamp it on the room.
 */
export function markClearings(grid, rows, cols) {
  const open = (r, c) => CLEARING_TYPES.has(grid?.[r]?.[c]?.type);
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const room = grid?.[row]?.[col];
      if (!room || !CLEARING_TYPES.has(room.type)) continue;
      room.clearing = {
        n: open(row - 1, col), e: open(row, col + 1),
        s: open(row + 1, col), w: open(row, col - 1),
      };
    }
  }
}

/**
 * Place a campsite room in the middle portion of the grid for healing
 * Exact copy from raid.js line 425
 */
export function placeCampsite(grid, gridRows, gridCols, trunk = new Set()) {
  // The rest stop may not sit on the trunk. It used to PREFER road cells
  // (candidates.road below), which was harmless while roads were scattered
  // filler — now that the whole through-route is road rooms, it meant the camp
  // and its water landed on the highway and overwrote it, leaving a hole in
  // the route and a roadway drawn across a lake.
  const onTrunk = (row, col) => trunk.has(`${row},${col}`);
  // Middle portion: columns from 1/3 to 2/3 of grid
  const minCol = Math.floor(gridCols / 3);
  const maxCol = Math.floor(2 * gridCols / 3);

  // Try to place in middle rows (avoid top/bottom edges)
  const minRow = Math.floor(gridRows / 3);
  const maxRow = Math.floor(2 * gridRows / 3);

  // Collect candidate cells by priority: empty > road > no priority > high priority > any
  const candidates = {
    empty: [],
    road: [],
    normal: [],
    high: [],
    any: []
  };

  for (let row = minRow; row <= maxRow; row++) {
    for (let col = minCol; col <= maxCol; col++) {
      const room = grid[row][col];

      if (onTrunk(row, col)) {
        continue;
      } else if (!room) {
        candidates.empty.push({ row, col });
      } else if (room.type === 'road') {
        candidates.road.push({ row, col });
      } else if (room.type !== 'start' && room.type !== 'exit') {
        if (!room.priority) {
          candidates.normal.push({ row, col });
        } else if (room.priority === 'high') {
          candidates.high.push({ row, col });
        } else if (room.priority !== 'critical') {
          candidates.any.push({ row, col });
        }
      }
    }
  }

  // Try each priority group in order
  const priorityOrder = [candidates.empty, candidates.road, candidates.normal, candidates.high, candidates.any];

  let campsiteRow, campsiteCol;
  for (const group of priorityOrder) {
    if (group.length > 0) {
      // Pick random cell from this priority group
      const cell = group[Math.floor(Math.random() * group.length)];
      grid[cell.row][cell.col] = createCampsite(cell.row, cell.col);
      campsiteRow = cell.row;
      campsiteCol = cell.col;
      break;
    }
  }

  // Place water adjacent to campsite
  if (campsiteRow !== undefined && campsiteCol !== undefined) {
    const adjacentCells = [
      { row: campsiteRow - 1, col: campsiteCol },
      { row: campsiteRow + 1, col: campsiteCol },
      { row: campsiteRow, col: campsiteCol - 1 },
      { row: campsiteRow, col: campsiteCol + 1 }
    ].filter(pos => pos.row >= 0 && pos.row < gridRows && pos.col >= 0 && pos.col < gridCols
        && !onTrunk(pos.row, pos.col));

    // Water takes the best neighbour it can get, in the same priority order the
    // campsite uses above. It used to accept only empty/road/blocked, which
    // worked while roads were scattered filler — once the roads became the
    // trunk (and the trunk became off-limits) that left almost nothing legal
    // beside a campsite, and the fishing room all but disappeared from raids.
    const rank = room => {
      if (!room) return 0;
      if (room.type === 'road' || room.type === 'blocked') return 1;
      if (room.type === 'start' || room.type === 'exit' || room.type === 'campsite') return Infinity;
      if (room.priority === 'critical' || room.priority === 'high') return Infinity;
      return 2;                                   // an ordinary encounter, yielded reluctantly
    };
    const spot = adjacentCells
      .map(pos => ({ pos, rank: rank(grid[pos.row][pos.col]) }))
      .filter(x => x.rank !== Infinity)
      .sort((a, b) => a.rank - b.rank)[0];
    if (spot) grid[spot.pos.row][spot.pos.col] = createWater(spot.pos.row, spot.pos.col);
  }
}

/**
 * What an ordinary cell of this district is built of. Weighted draw over the
 * archetype's recipe mix; an unknown district falls through to the room's own
 * name-derived recipe by returning nothing.
 */
export function pickDistrictRecipe(zone, rand = Math.random) {
  const mix = config.zoneArchetypes[zone]?.recipes;
  if (!mix) return null;
  const entries = Object.entries(mix);
  let roll = rand() * entries.reduce((sum, [, w]) => sum + w, 0);
  for (const [recipe, weight] of entries) {
    roll -= weight;
    if (roll < 0) return recipe;
  }
  return entries[entries.length - 1][0];
}

/**
 * Add alternate routes and fill remaining spaces with diverse rooms
 * Exact copy from raid.js line 499
 */
export function addAlternateRoutes(grid, zones, region, regionKey, gridRows, gridCols) {
  // Create a diverse pool of rooms to avoid clustering
  const roomPool = createDiverseRoomPool(region, gridRows, gridCols);
  let roomIndex = 0;

  // Fill remaining empty spaces with diverse rooms
  for (let row = 0; row < gridRows; row++) {
    for (let col = 0; col < gridCols; col++) {
      if (!grid[row][col]) {
        // Get next room from diverse pool, avoiding adjacent duplicates
        const roomData = getNextDiverseRoom(grid, row, col, roomPool, roomIndex, gridRows, gridCols);
        roomIndex = (roomIndex + 1) % roomPool.length;

        const currentZone = determineZone(row, col, zones);
        const roomWithNames = addRoomNames(roomData, regionKey, currentZone, row, col);

        grid[row][col] = {
          ...roomWithNames,
          row: row,
          col: col,
          zone: currentZone,
          // What this cell is BUILT of comes from the district, not from the
          // room standing on it — see zoneArchetypes for why. Only the fill
          // takes it: the checkpoints and high-value targets the map places by
          // hand are landmarks, and a landmark has to look like itself.
          recipe: pickDistrictRecipe(currentZone),
          priority: 'normal'
        };
      }
    }
  }
}

/**
 * Create a diverse pool of rooms with balanced distribution
 * Exact copy from raid.js line 528
 */
export function createDiverseRoomPool(region, gridRows, gridCols) {
  const totalRooms = gridRows * gridCols;
  const roomPool = [];

  // Calculate target counts for each room type
  const gateCount = Math.floor(totalRooms * 0.3);
  const hazardCount = Math.floor(totalRooms * 0.7);

  // Add gates with even distribution
  for (let i = 0; i < gateCount; i++) {
    const gateTemplate = region.gates[i % region.gates.length];
    roomPool.push({
      type: 'gate',
      ...gateTemplate
    });
  }

  // Add hazards with even distribution
  for (let i = 0; i < hazardCount; i++) {
    const hazardTemplate = region.hazards[i % region.hazards.length];
    roomPool.push({
      type: 'hazard',
      ...hazardTemplate
    });
  }

  // Shuffle the pool to ensure random but diverse distribution
  return shuffleArray(roomPool);
}

/**
 * Get next room that's different from adjacent rooms
 * Exact copy from raid.js line 559
 */
export function getNextDiverseRoom(grid, row, col, roomPool, startIndex, gridRows, gridCols) {
  const adjacentRooms = getAdjacentRoomNames(grid, row, col, gridRows, gridCols);

  // Try to find a room that's not adjacent to identical rooms
  for (let i = 0; i < roomPool.length; i++) {
    const roomIndex = (startIndex + i) % roomPool.length;
    const room = roomPool[roomIndex];

    if (!adjacentRooms.includes(room.name)) {
      return room;
    }
  }

  // If all rooms are adjacent, just return the next one
  return roomPool[startIndex % roomPool.length];
}

/**
 * Get names of adjacent rooms to avoid duplicates
 * Exact copy from raid.js line 577
 */
export function getAdjacentRoomNames(grid, row, col, gridRows, gridCols) {
  const adjacent = [];
  const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]]; // up, down, left, right

  directions.forEach(([dr, dc]) => {
    const newRow = row + dr;
    const newCol = col + dc;
    if (newRow >= 0 && newRow < gridRows && newCol >= 0 && newCol < gridCols) {
      const room = grid[newRow][newCol];
      if (room && room.name) {
        adjacent.push(room.name);
      }
    }
  });

  return adjacent;
}

/**
 * Which district a cell is in. generateDistricts assigns every cell, so the
 * fallback is only reached by a save written before districts existed.
 */
export function determineZone(row, col, zones) {
  return zones?.cells?.[row]?.[col] || 'peripheral';
}

/**
 * Fill any remaining null cells with road rooms to ensure complete grid population
 * Exact copy from raid.js line 832
 */
export function fillEmptyCells(grid, gridRows, gridCols) {
  for (let row = 0; row < gridRows; row++) {
    for (let col = 0; col < gridCols; col++) {
      if (grid[row] && grid[row][col] === null) {
        grid[row][col] = createRoad(row, col);
      }
    }
  }
}

// ============================================================================
// PATH & GAMEPLAY CALCULATIONS
// ============================================================================

/**
 * Validate if a path is valid in a raid grid
 * Exact copy from raid.js line 842
 */
export function isValidPath(path, gridData) {
  if (path.length < 2) return false;

  const start = path[0];
  const end = path[path.length - 1];

  // Must start at start position and end at end position
  if (start.row !== gridData.startPos.row || start.col !== gridData.startPos.col) return false;
  if (end.row !== gridData.endPos.row || end.col !== gridData.endPos.col) return false;

  // Check if path is continuous (adjacent moves only)
  for (let i = 1; i < path.length; i++) {
    const prev = path[i - 1];
    const curr = path[i];
    const rowDiff = Math.abs(curr.row - prev.row);
    const colDiff = Math.abs(curr.col - prev.col);

    // Allow only orthogonal moves (not diagonal)
    if ((rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1)) {
      continue;
    } else {
      return false;
    }
  }

  return true;
}

/**
 * Determine room skill status based on team capabilities
 * Exact copy from raid.js line 871
 */
export function getRoomSkillStatus(room, teamSkills) {
  if (room.type === 'blocked')
    return;

  const primarySkillLevel = teamSkills[room.primary] || 0;
  const supportSkillLevels = room.support.map(skill => teamSkills[skill] || 0);
  const totalSupportLevel = supportSkillLevels.reduce((sum, level) => sum + level, 0);

  // Check if primary skill is covered
  const primaryCovered = primarySkillLevel > 0;

  // Check support coverage - require at least some level in each support skill
  const supportCovered = room.support.length === 0 || room.support.every(skill => (teamSkills[skill] || 0) > 0);

  if (primaryCovered && supportCovered) {
    return {
      class: 'skill-covered',
      indicator: 'covered',
      symbol: '✓'
    };
  } else if (primaryCovered) {
    return {
      class: 'skill-match',
      indicator: 'partial',
      symbol: '▲'
    };
  } else {
    return {
      class: 'skill-missing',
      indicator: 'missing',
      symbol: '✗'
    };
  }
}

/**
 * Count non-road steps in a path
 * Exact copy from raid.js (countNonRoadSteps)
 */
export function countNonRoadSteps(path) {
    if (!path || path.length === 0) return 0;

    return path.filter(step => {
        return step.room && (step.room.type === 'gate' || step.room.type === 'hazard');
    }).length;
}

/**
 * Get step number in path
 * Exact copy from raid.js (getStepNumber)
 */
export function getStepNumber(path, index) {
    if (!path || index < 0 || index >= path.length) return index + 1;

    let stepNumber = 1;
    for (let i = 0; i <= index; i++) {
        const room = path[i].room;
        // Only count gates and hazards as actual steps
        if (room && (room.type === 'gate' || room.type === 'hazard')) {
            if (i === index) return stepNumber;
            stepNumber++;
        } else if (i === index) {
            return '-'; // Roads, start, exit, water and campsite don't get numbers
        }
    }
    return stepNumber;
}

/**
 * Heal the team: split the base healing across injured members, then top each
 * one off with the raid owner's HP consumables (e.g. campsite recovery).
 *
 * Consumable use (biggest heal first): for each injured member, try each HP
 * consumable the player carries in descending HP value; apply one when the
 * member's remaining deficit exceeds the consumable's value (so none is
 * wasted on a small deficit), adding its HP and spending one from inventory.
 */
export function healTeam(team, totalHealing) {
  const injuredMembers = team.filter(member => member.isAlive && member.currentHp < member.maxHp);
  if (injuredMembers.length === 0) return;

  // 1. Consumables FIRST: top each member off with the raid owner's HP
  //    consumables (biggest heal first, one of each, only when the deficit
  //    exceeds the value so none is wasted / overshoots maxHp).
  const inventory = gameState.character?.inventory || {};
  const hpConsumables = getAllItems()
    .filter(item => item.restoresHp > 0)
    .sort((a, b) => b.restoresHp - a.restoresHp);

  const used = {};
  injuredMembers.forEach(member => {
    for (const consumable of hpConsumables) {
      if ((inventory[consumable.id] || 0) > 0 && member.maxHp - member.currentHp > consumable.restoresHp) {
        member.currentHp += consumable.restoresHp;
        inventory[consumable.id] -= 1;
        if (inventory[consumable.id] <= 0) delete inventory[consumable.id];
        used[consumable.name] = (used[consumable.name] || 0) + 1;
      }
    }
  });

  // 2. Base healing AFTER items, split by remaining need (not equally): each
  //    member gets a share proportional to its share of the total missing HP,
  //    so it flows to the worst-hurt and fills the small residual deficits the
  //    consumables' no-overshoot rule left behind. Clamped to maxHp.
  if (totalHealing > 0) {
    const deficits = injuredMembers.map(m => m.maxHp - m.currentHp);
    const totalDeficit = deficits.reduce((sum, d) => sum + d, 0);
    if (totalDeficit > 0) {
      injuredMembers.forEach((member, i) => {
        const share = Math.floor(totalHealing * deficits[i] / totalDeficit);
        member.currentHp = Math.min(member.maxHp, member.currentHp + share);
      });
    }
  }

  const usedList = Object.entries(used);
  if (usedList.length) {
    addRaidMessage(`🩹 Used ${usedList.map(([name, qty]) => `${qty}× ${name}`).join(', ')} to patch up the team`, 'info',
      { notify: false, stepIndex: gameState.currentRaid?.raidInProgress?.currentRoomIndex });
  }
}

/**
 * Check if team has any alive members
 * Exact copy from raid.js (hasAliveMember)
 */
export function hasAliveMember(team) {
  //console.log("CHECKING FOR ALIVE TEAM", team);
  if (!team || !Array.isArray(team)) return false;
  return team.some(member => member.isAlive);
}

/**
 * Calculate raid results
 * Exact copy from raid.js line 3470 (calculateRaidResults)
 */
export function calculateRaidResults(success, roomsCompleted, totalRooms, region, team, tier) {
  let creditsEarned = 0;

  if (success) {
      // Full success bonus (increased for monthly progression)
      creditsEarned = tier * 150 + (roomsCompleted * 40);

      // Bonus for team survival
      const survivingMembers = team.filter(member => member.isAlive).length;
      const survivalBonus = Math.floor((survivingMembers / team.length) * 50);
      creditsEarned += survivalBonus;
  } else {
      // Partial rewards based on completion
      const completionRatio = roomsCompleted / (totalRooms - 1);
      creditsEarned = Math.floor((tier * 100) * completionRatio);
  }

  // Apply credit scaling to raid completion bonus
  creditsEarned = applyCreditsScaling(creditsEarned, tier);

  return {
      creditsEarned: creditsEarned,
      success: success,
      completionRatio: roomsCompleted / (totalRooms - 1)
  };
}

/**
 * Apply post-raid injuries
 * Exact copy from raid.js (applyPostRaidInjuries) - PURE VERSION
 */
export function applyPostRaidInjuries(team) {
    team.forEach(member => {
        if (member.type === 'player') {
            // Update player health
            gameState.character.currentHp = member.currentHp;
        } else if (member.type === 'contractor') {
            // Find the original contractor and update health
            const originalContractor = gameState.currentRaid.hiredContractors.find(c => c.id === member.id);
            if (originalContractor) {
                originalContractor.currentHp = member.currentHp;
                originalContractor.maxHp = member.maxHp;

                // If contractor died, they become unavailable for a while
                if (!member.isAlive) {
                    originalContractor.injured = true;
                    originalContractor.recoveryTime = Date.now() + (24 * 60 * 60 * 1000); // 24 hours
                }
            }
        }
    });
}

/**
 * Calculate raid XP
 * Exact copy from raid.js (calculateRaidXP)
 */
export function calculateRaidXP(success, roomsCompleted, totalRooms, region, team, tier) {
    // Reduced for long-term idle progression (1-5 months to reach Tier 3)
    let baseXP = tier * 15; // 15/30/45 base XP

    // Room completion bonus
    const roomBonus = roomsCompleted * 3;

    // Success multiplier
    const successMultiplier = success ? 1.5 : 0.8;

    // Difficulty bonus based on team size vs difficulty
    const teamSize = team.filter(member => member.isAlive).length;
    const difficultyBonus = Math.max(0, (tier * 2) - teamSize) * 3;

    // Full path completion bonus
    const fullPathBonus = (roomsCompleted === totalRooms - 1) ? 10 : 0;

    // Command-era tier weighting (progressionScaling SSOT): pushing tiers
    // is the leveling ladder - T2/T3 raids pay multiples of T1
    const tierMultiplier = config.progressionScaling?.raidXpTierMultipliers?.[tier] || 1;
    const totalXP = Math.floor((baseXP + roomBonus + difficultyBonus + fullPathBonus) * successMultiplier * tierMultiplier);
    return Math.max(5, totalXP); // Minimum 5 XP
}

/**
 * Convert duration in seconds to energy cost
 * Exact copy from raid.js (durationToEnergy)
 */
export function durationToEnergy(durationInSeconds) {
  if (!durationInSeconds || durationInSeconds <= 0) return 0;
  return Math.ceil(durationInSeconds / 120); // 1 energy per 2 minutes, from character.js
}

/**
 * Calculate energy cost for a raid
 * Exact copy from raid.js (calculateEnergyCost)
 */
export function calculateEnergyCost(tier, path) {
    // Calculate total duration in seconds from all rooms in path
    //console.log('Calculating energy cost for path:', path);
    const totalDuration = path.reduce((sum, step) => sum + (step.room.baseDuration ? step.room.baseDuration : 0), 0);

    // Energy cost: Base cost per tier + 1 energy per 40 seconds of raid duration.
    // The Sync Regulator Watch trims the total (raid-energy-reduction).
    // Base: T1: 5, T2: 10, T3: 15
    // Realistic T3 worst: 21-room path with mix of gates (120s) and hazards (60s)
    // Example: 10 gates + 11 hazards = 1200 + 660 = 1860s
    // Cost: 15 + (1860/40) = 15 + 47 = 62 energy (well under 100)
    const baseCost = 5 + (tier - 1) * 5;
    const durationCost = durationToEnergy(totalDuration);
    const reduction = 1 - getUtilityArtifactBonus('raid-energy-reduction') / 100;
    const energyCost = Math.max(1, Math.ceil((baseCost + durationCost) * reduction));
    //console.log(`Calculated energy cost for tier ${tier} and duration ${totalDuration}s: Base ${baseCost} + Duration ${durationCost} = Total ${energyCost}`);

    return energyCost;
}

// ============================================================================
// CONTRACTOR STATE CHECKING
// ============================================================================

/**
 * Helper export function to check if a contractor is reserved (single source of truth)
 * Exact copy from raid.js line 920
 */
export function isContractorReserved(contractorId) {
    return gameState.currentRaid.reservedContractors.some(c => c.id == contractorId);
}

/**
 * Check if a contractor is hired
 * Exact copy from raid.js line 924
 */
export function isContractorHired(contractorId) {
    return gameState.currentRaid.hiredContractors.some(c => c.id == contractorId);
}

// ============================================================================
// PATH VALIDATION (Additional)
// ============================================================================

/**
 * Check if a cell can be added to the current raid path
 * Exact copy from raid.js line 694
 */
export function canAddToPath(row, col) {
    const gridData = gameState.currentRaid.currentRaidGrid;

    // First click must be start position
    if (gameState.currentRaid.selectedPath.length === 0) {
        return row === gridData.startPos.row && col === gridData.startPos.col;
    }

    // If path already ends at exit, can't add more rooms
    if (gameState.currentRaid.selectedPath.length > 0) {
        const lastPos = gameState.currentRaid.selectedPath[gameState.currentRaid.selectedPath.length - 1];
        const lastRoom = gridData.grid[lastPos.row][lastPos.col];
        if (lastRoom.type === 'exit') {
            return false;
        }
    }

    // Can't add the same room twice
    const isAlreadyInPath = gameState.currentRaid.selectedPath.some(p => p.row === row && p.col === col);
    if (isAlreadyInPath) {
        return false;
    }

    // Check if target cell is blocked
    const targetRoom = gridData.grid[row][col];
    if (targetRoom && targetRoom.type === 'blocked') {
        return false;
    }

    // Check maximum path length: rows + cols - 1 (excluding roads)
    // BUT allow connection to exit even at max steps since exit doesn't count
    const maxPathLength = (gridData.rows || gridData.size) + (gridData.cols || gridData.size) - 1;
    const currentStepCount = countNonRoadSteps(gameState.currentRaid.selectedPath);

    if (currentStepCount >= maxPathLength) {
        // At max steps, only allow roads/campsite (0 cost) or exit
        if (targetRoom && (targetRoom.type === 'exit' || targetRoom.type === 'road' || targetRoom.type === 'campsite' || targetRoom.type === 'water')) {
            // Allow 0-cost moves and exit
        } else {
            return false; // Block gates/hazards
        }
    }

    // Must be adjacent to last position in path
    const lastPos = gameState.currentRaid.selectedPath[gameState.currentRaid.selectedPath.length - 1];
    const rowDiff = Math.abs(row - lastPos.row);
    const colDiff = Math.abs(col - lastPos.col);

    // Only allow orthogonal moves (not diagonal)
    return (rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1);
}

// ============================================================================
// SKILL ANALYSIS (gameState-dependent versions)
// ============================================================================

/**
 * Extract required skills from selected raid path
 * Exact copy from raid.js line 1029
 */
export function getPathRequiredSkills() {
    const skills = new Set();

    gameState.currentRaid.selectedPath.forEach(step => {
        const room = step.room;
        if (room.type !== 'start' && room.type !== 'exit') {
            skills.add(room.primary);
            room.support.forEach(skill => skills.add(skill));
        }
    });

    return skills;
}

/**
 * Extract detailed skill requirements from selected raid path
 * Exact copy from raid.js line 1043
 */
export function getDetailedPathSkills() {
    const skillDetails = {};

    gameState.currentRaid.selectedPath.forEach(step => {
        const room = step.room;
        if (room.type !== 'start' && room.type !== 'exit') {
            // Track primary skills
            if (!skillDetails[room.primary]) {
                skillDetails[room.primary] = {
                    name: getSkillDisplayName(room.primary),
                    isPrimary: true,
                    isSupport: false,
                    rooms: []
                };
            }
            skillDetails[room.primary].rooms.push(room.name);

            // Track support skills
            room.support.forEach(skill => {
                if (!skillDetails[skill]) {
                    skillDetails[skill] = {
                        name: getSkillDisplayName(skill),
                        isPrimary: false,
                        isSupport: true,
                        rooms: []
                    };
                } else if (!skillDetails[skill].isSupport) {
                    skillDetails[skill].isSupport = true;
                }
                skillDetails[skill].rooms.push(`${room.name} (support)`);
            });
        }
    });

    return skillDetails;
}

/**
 * Calculate team skill levels from raid team
 * Exact copy from raid.js line 1080
 */
export function getTeamSkillLevels() {
    const raidTeam = getRaidTeam();
    const teamSkills = {};

    //console.log('Calculating team skills from raid team:', raidTeam);
    raidTeam.forEach(member => {
        Object.entries(member.skills).forEach(([skill, level]) => {
            teamSkills[skill] = Math.max((teamSkills[skill] || 0), level);
        });
    });

    return teamSkills;
}

/**
 * Distinct technique ids covered by the team's equipped skill gear
 * (player + reserved/hired contractors). Handles both raw objects
 * (equippedTechniqueGear map) and team snapshots (equippedTechniques array).
 */
export function getTeamEquippedTechniques() {
    const techniques = new Set();
    getRaidTeam().forEach(member => {
        const list = member.equippedTechniques || Object.keys(member.equippedTechniqueGear || {});
        list.forEach(t => techniques.add(t));
    });
    return techniques;
}

/**
 * Expected success-chance bonus from equipped gear for a skill's checks:
 * events pick uniformly among the skill's techniques, so covering k of n
 * techniques is worth bonus * k/n in expectation (exact at full coverage).
 */
export function getGearExpectedBonus(skillId, teamTechniques) {
    if (!teamTechniques || teamTechniques.size === 0) return 0;
    let total = 0, covered = 0;
    Object.values(config.skillTechniques).forEach(list => list.forEach(t => {
        if (t.skill === skillId) {
            total++;
            if (teamTechniques.has(t.id)) covered++;
        }
    }));
    if (total === 0 || covered === 0) return 0;
    const bonus = (getEffect('technique-success-bonus')?.value || 0) / 100;
    return bonus * covered / total;
}

// ============================================================================
// UTILITY & TEXT GENERATION FUNCTIONS
// ============================================================================

/**
 * Generate a unique raid ID
 * Exact copy from raid.js line 3205
 */
export function generateRaidId() {
    return `raid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Select random element from array (roll narration)
 * Exact copy from raid.js line 3209
 */
export function rollNarrate(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Generate a pickup line/narrative for item discovery
 * Exact copy from raid.js line 3217
 */
export function narratePickup(itemName, ctx = {}) {
    const itemSearch = Object.values(getAllItems()).filter(item => item.name === itemName);
    if (itemSearch.length === 0) return ""; // silent if no item found
    const item = itemSearch[0];

    const base = config.raidFlavor[item.type]?.[item.tier];
    if (!base) return ""; // silent if no template
    const core = rollNarrate(base);

    // 20% chance to append a faction "tag" (keeps lines short)
    let tag = "";
    if (ctx.faction && config.factionVoice[ctx.faction] && Math.random() < 0.2) {
        tag = " " + rollNarrate(config.factionVoice[ctx.faction]);
    }

    // 20% chance to weave a tiny context shard
    let shard = "";
    if (Math.random() < 0.2) {
        const a = rollNarrate(config.contextShards.action);
        const l = ctx.shortLocation || rollNarrate(config.contextShards.location);
        shard = ` (${a} from ${l})`;
    }

    // keep under ~80 chars
    let line = `${core}${tag}${shard}`;
    if (line.length > 84) line = core + (tag ? tag : "");
    return line;
}

// ============================================================================
// LOOT & DATA MANIPULATION FUNCTIONS
// ============================================================================

// getItemPoolForRarity lives in eventSystem-core.js (single authoritative
// implementation: skill-scoped material pools, epic stim filtering,
// skill-tied blueprints); re-exported here for existing raid.js imports.
export { getItemPoolForRarity };

/**
 * Generate bonus loot at specific rarity tier (drop chance already checked by caller)
 * Exact copy from raid.js line 2861
 */
export function generateBonusLoot(room, tier, rarity) {
    const loot = {};
    const quantityRange = getRarityQuantityRange(rarity, tier, room.type);
    const quantity = Math.round(Math.random() * (quantityRange.max - quantityRange.min)) + quantityRange.min;
    const itemPool = getItemPoolForRarity(room, tier, rarity);

    // Select random item from pool
    if (itemPool.length > 0) {
        const item = itemPool[Math.floor(Math.random() * itemPool.length)];
        loot[item] = quantity;
    }

    return loot;
}

/**
 * Get possible room loot array
 * Exact copy from raid.js line 2279
 */
export function getPossibleRoomLoot(room, regionTier) {
    if (room.type === 'start' || room.type === 'exit' || room.type === 'road') {
        return [];
    }

    const possibleLoot = [];

    if (room.type === 'gate') {
        // Rare materials
        possibleLoot.push(...getItemPoolForRarity(room, regionTier, 'rare').slice(0, 3));

        // Blueprints
        possibleLoot.push(config.itemTypes.blueprint.name);

        // Story relics (tier 4+)
        possibleLoot.push(...getItemPoolForRarity(room, regionTier, 'unique').slice(0, 2));

    } else {
        // Common materials
        possibleLoot.push(...getItemPoolForRarity(room, regionTier, 'common').slice(0, 2));

        // Rare materials (tier 3+)
        if (regionTier >= 3) {
            possibleLoot.push(...getItemPoolForRarity(room, regionTier, 'rare').slice(0, 2));
        }

        // Consumables by tier
        const tierRarity = regionTierToRarity(regionTier);
        const consumables = getItemsByType('consumable', tierRarity);
        if (consumables.length === 0) {
            consumables.push(...getItemsByType('consumable', 'common'));
        }
        possibleLoot.push(...consumables.slice(0, 2));

        // Relics by tier
        const relics = getItemsByType('relic', tierRarity);
        if (relics.length === 0) {
            relics.push(...getItemsByType('relic', 'common'));
        }
        possibleLoot.push(...relics.slice(0, 2));
    }

    // All rooms - credits
    possibleLoot.push('Credits');

    return possibleLoot;
}

// ============================================================================
// CALCULATION FUNCTIONS (Game Logic)
// ============================================================================

/**
 * Calculate combined team skills from multiple members (only alive members count)
 * Exact copy from raid.js line 2197
 */
export function calculateTeamSkills(team) {
    const skills = {};

    team.forEach(member => {
        if (member.isAlive) {
            Object.entries(member.skills).forEach(([skill, level]) => {
                skills[skill] = Math.max(skills[skill] || 0, level);
            });
        }
    });

    return skills;
}

/**
 * Where the odds stop being comfortable, and where they stop being sensible.
 *
 * Exported so the mark and the colour beside it cannot drift: the raid log
 * prints a percentage AND a symbol on the same line, and until these were
 * shared they answered to different scales entirely.
 */
export const ODDS_STRONG = 0.7;
export const ODDS_RISKY = 0.4;

/**
 * The at-a-glance risk mark for a room, from the odds the game will actually
 * roll against — the same number the log prints as `🎯 N%`.
 *
 * This replaced a mark derived from skill COVERAGE, which measured something
 * else and said so out loud: coverage asks "is every skill present at all",
 * ignoring levels, so a room could show ▲ beside 97% (primary far above the
 * target, one support skill at zero) or ✓ beside 41% (every skill present, all
 * of them level 1 against a high target). Two three-band scales on one line,
 * disagreeing, and the one the player could check was the honest one.
 *
 * Coverage is still the right answer to "WHY are my odds like this", and it is
 * still what the room tooltip says, in words, where there is room to explain.
 * The glance is risk; the detail is the reason.
 */
export function getRoomOddsStatus(successChance) {
  if (successChance >= ODDS_STRONG) return { indicator: 'strong', symbol: '✓' };
  if (successChance >= ODDS_RISKY) return { indicator: 'risky', symbol: '▲' };
  return { indicator: 'poor', symbol: '✗' };
}

/**
 * Calculate success chance for a single room without rolling
 * Exact copy from raid.js line 2212
 * Note: Falls back to gameState if raidTier not provided (should refactor for purity)
 */
export function calculateRoomSuccessChance(room, teamSkills, raidTier = null, teamTechniques = null) {
    if (room.type === 'start' || room.type === 'exit' || room.type === 'road') {
        return 1.0; // Always succeed for special rooms
    }

    // Get raid tier from current grid if not provided
    if (!raidTier && gameState.currentRaid.currentRaidGrid) {
        raidTier = gameState.currentRaid.currentRaidGrid.tier;
    }
    raidTier = raidTier || 1; // Default to tier 1

    // Get target skill level for this room type and tier
    const targetSkill = room.type == 'gate' ? config.raidTierData[raidTier].gateSkillLevel : config.raidTierData[raidTier].hazardSkillLevel;
    const primarySkill = teamSkills[room.primary] || 0;
    // Calculate support bonus with 15% cap to prevent exploits
    const rawSupportBonus = room.support.reduce((sum, skill) => sum + (teamSkills[skill] || 0), 0) * 0.1;
    const supportBonus = Math.min(rawSupportBonus, 0.15);

    //console.log(`Calculating success chance for room ${room.name} (Type: ${room.type}, Tier: ${raidTier})`);
    //console.log(` - Target Skill: ${targetSkill}`);
    //console.log(` - Primary Skill (${room.primary}): ${primarySkill}`);
    //console.log(` - Support Bonus: ${supportBonus.toFixed(2)} (capped from ${rawSupportBonus.toFixed(2)})`);

    // Improved progression-based success chance calculation
    // Base 76% chance when skill equals target, +/- 5% per skill level
    // difference; equipped gear adds its expected bonus (see
    // getGearExpectedBonus) and the equipped skill artifact its flat +5%
    // when it matches the room's primary skill (exact, not an expectation)
    const skillDifference = primarySkill - targetSkill;
    const gearBonus = getGearExpectedBonus(room.primary, teamTechniques);
    const artifactBonus = getSkillArtifactBonus(room.primary) / 100;
    let successChance = 0.76 + (skillDifference * 0.05) + supportBonus + gearBonus + artifactBonus;

    // Ensure minimum 20% chance
    successChance = Math.max(0.20, successChance);
    // Cap at 97%
    successChance = Math.min(0.97, successChance);

    return successChance;
}

/**
 * Calculate overall success chance for the entire raid path
 * Exact copy from raid.js line 2249
 * Note: Falls back to gameState if path/tier not provided (should refactor for purity)
 */
export function calculatePathSuccessChance(path = null) {
    if (!path) {
        path = gameState.currentRaid.selectedPath;
    }
    if (!path || path.length === 0) {
        return 0;
    }

    const teamSkills = getTeamSkillLevels();
    const teamTechniques = getTeamEquippedTechniques();
    const raidTier = gameState.currentRaid.currentRaidGrid ? gameState.currentRaid.currentRaidGrid.tier : 1;

    let overallChance = 1.0;

    // Calculate compound probability across all rooms that require skill checks
    path.forEach(step => {
        const room = step.room;
        if (room.type !== 'start' && room.type !== 'exit' && room.type !== 'road' && room.type !== 'campsite' && room.type !== 'water') {
            const roomChance = calculateRoomSuccessChance(room, teamSkills, raidTier, teamTechniques);
            //console.log(overallChance, '*', roomChance, '=', overallChance * roomChance);
            overallChance *= roomChance;
        }
    });

    return overallChance;
}

// ============================================================================
// TEAM FUNCTIONS
// ============================================================================

// Initialize raid team with health and status
export function initializeRaidTeam() {
    const team = [];

    // Add player character
    team.push({
        id: 'player',
        name: gameState.character.name,
        type: 'player',
        faction: gameState.character.faction,
        skills: {...gameState.character.skills},
        // Equipped skill-gear technique ids: drive event variant selection
        // and the per-technique success bonus during the raid
        equippedTechniques: Object.keys(gameState.character.equippedTechniqueGear || {}),
        maxHp: gameState.character.maxHp,
        currentHp: gameState.character.currentHp || gameState.character.maxHp,
        isAlive: true,
        injuries: []
    });

    // Add hired contractors
    gameState.currentRaid.hiredContractors.forEach(contractor => {
        team.push({
            id: contractor.id,
            name: contractor.name,
            type: 'contractor',
            faction: contractor.faction,
            skills: {...contractor.skills},
            equippedTechniques: Object.keys(contractor.equippedTechniqueGear || {}),
            maxHp: contractor.maxHp || 100,
            currentHp: contractor.currentHp || contractor.maxHp || 100,
            isAlive: true,
            injuries: [],
            cost: contractor.cost
        });
    });

    // Set current HP to max for all members
    team.forEach(member => {
        if (member.currentHp === null) {
            member.currentHp = member.maxHp;
        }
    });

    return team;
}

export function getRaidTeam() {
    const team = [];
    //console.log("CREATING TEAM", gameState.currentRaid.hiredContractors);
    if (gameState.character) {
        team.push(gameState.character);
    }
    
    if (gameState.currentRaid) {
        if (gameState.currentRaid.hiredContractors && gameState.currentRaid.hiredContractors.length > 0) {
            gameState.currentRaid.hiredContractors.forEach(contractor => {
                team.push(contractor);
            });
        }
        else if (gameState.currentRaid.reservedContractors && gameState.currentRaid.reservedContractors.length > 0) {
            gameState.currentRaid.reservedContractors.forEach(contractor => {
                team.push(contractor);
            });
        }
    }

    //console.log("CREATED TEAM OF ", team.length);
    return team;
}

// ============================================================================
// RAID SETUP FUNCTIONS
// ============================================================================

// Check if raid can be started
export function validateRaidSetup() {
    if (!gameState.currentRaid.currentRaidGrid || !gameState.character || gameState.currentRaid.selectedPath.length === 0) 
      return false;

    // Check if current character is on marketplace
    if (gameState.character.isMarketplace) {
        addNotification('Cannot start raids with a character that is contracted out on the marketplace', 'error');
        return false;
    }

    // Validate path
    if (!isValidPath(gameState.currentRaid.selectedPath, gameState.currentRaid.currentRaidGrid)) {
        const errorMsg = 'Invalid path! Make sure you start at the entry portal and end at the exit portal.';
        if (typeof alert !== 'undefined') {
            alert(errorMsg);
        } else {
            console.warn(errorMsg);
        }
        return false;
    }

    // Check energy requirements
    const tier = gameState.currentRaid.currentRaidGrid.tier;
    const energyCost = calculateEnergyCost(tier, gameState.currentRaid.selectedPath);

    if (gameState.player.energy < energyCost) {
        const errorMsg = `Not enough energy! This raid requires ${energyCost} energy. You have ${gameState.player.energy}. Energy regenerates over time.`;
        if (typeof alert !== 'undefined') {
            alert(errorMsg);
        } else {
            console.warn(errorMsg);
        }
        return false;
    }
    
    return true;
}

export async function startRaidCore() {
  // Consume energy
  const tier = gameState.currentRaid.currentRaidGrid.tier;
  const energyCost = calculateEnergyCost(tier, gameState.currentRaid.selectedPath);
  gameState.player.energy -= energyCost;

  // Hire reserved contractors (no upfront cost - they take percentage of rewards)
  gameState.currentRaid.hiredContractors = [...gameState.currentRaid.reservedContractors];

  // Update database for multiplayer contractors
  const currentCharacter = gameState.characters?.find(c => c.id === gameState.activeCharacterId);
  if (currentCharacter) {
      for (const contractor of gameState.currentRaid.hiredContractors) {
          if (contractor.multiplayerId) {
              const raidId = generateRaidId(); // Generate unique raid ID
              // tier lets the server reject a contract that would expire mid-raid
              await multiplayer.hireContractor(contractor.multiplayerId, currentCharacter.multiplayerId, raidId, tier);
          }
      }
  }

  // Clear reserved contractors
  gameState.currentRaid.reservedContractors = [];

  // Cold Drop: the convoy arrives already hurt. Applied HERE, at raid init and
  // to the whole team, rather than at team assembly — assembly runs while the
  // player is still planning, and docking HP there would let them heal back up
  // before launching and pocket the perk for free.
  const startHpFraction = blackoutModifier('startHpFraction', 1);
  if (startHpFraction < 1) {
      for (const member of getRaidTeam()) {
          member.currentHp = Math.max(1, Math.floor(member.maxHp * startHpFraction));
      }
  }

  // Record raid time
  gameState.lastRaidTime = Date.now();

  // Initialize raid state in gameState for persistence
  gameState.currentRaid.raidInProgress = {
      raidTeam: getRaidTeam(),
      teamSkills: null,
      raidSuccess: true,
      roomsCompleted: 0,
      lootGained: {},
      currentRoomIndex: 0,
      startTime: Date.now(),
      nextRoomTime: Date.now(), // Start room takes no time
      logEntries: [], // Store log entries for persistence
      progressiveLoot: {
          active: false,
          totalLoot: {},
          distributedLoot: {},
          intervals: [],
          nextLootTime: 0,
          roomName: ''
      },
      specialEvent: {
          active: false,
          scheduled: false,
          spawnTime: null,
          element: null,
          roomIndex: null
      }
  };

  // Calculate team skills
  gameState.currentRaid.raidInProgress.teamSkills = calculateTeamSkills(getRaidTeam());
}

// Called every second to process raid events and room transitions based on time
export function processCurrentRaidRoomCore() {
  if (!gameState.currentRaid.raidInProgress) return;
  const currentTime = Date.now();
  
  const raid = gameState.currentRaid.raidInProgress;
  const pathStep = gameState.currentRaid.selectedPath[raid.currentRoomIndex];

  // Process all scheduled events for the current room (skill-check, bonus loot, fishing) via unified event system
  let eventInfo = processScheduledEventsCore(gameState, getRaidTeam());
  if (eventInfo) {
    for (let i=0; i<10; i++) {
      if (eventInfo?.eventLifeCycle == 'start') {
        switch (eventInfo.eventType) {
          case 'skillCheck':
            // team read live, like the resolve call below: this loop can resolve
            // one event and start the next in the same tick, and a casualty in
            // between changes which techniques are available
            logEventProblem(eventInfo.eventData, raid.currentRoomIndex, gameState, getRaidTeam());
            break;
          case 'bonusLoot':
            presentBonusLootEvent(gameState);
            break;
          case 'fishing':
            presentFishingEvent(gameState);
            break;
        }
      }
      else if (eventInfo?.eventLifeCycle == 'end') {
        switch (eventInfo.eventType) {
          case 'skillCheck':
            processSkillCheckEvent(eventInfo.eventData, gameState, pathStep.room, getRaidTeam());
            break;
        }
      }
      
      eventInfo = processScheduledEventsCore(gameState, getRaidTeam());
    }
  }
  //processScheduledEvents(gameState, getRaidTeam());

  // No time check for simulation
  //console.log("time check debug:", gameState.simulated||"no-sim", currentTime, gameState.currentRaid.raidInProgress.nextRoomTime);
  if (gameState.simulated || currentTime >= gameState.currentRaid.raidInProgress.nextRoomTime) {
    const shouldContinue = processCurrentRoomCompletion();
    if (shouldContinue) {
        enterNextRoom();
    }
  }
}

// Process current room completion and move to next
export function processCurrentRoomCompletion() {
    const raid = gameState.currentRaid.raidInProgress;
    if (raid.currentRoomIndex >= gameState.currentRaid.selectedPath.length) {
        //somehow we exited but something went wrong
        finishRaidCore();
        return false; // Don't enter next room
    }

    const pathStep = gameState.currentRaid.selectedPath[raid.currentRoomIndex];
    const room = pathStep.room;

    // Count room events before cleanup (for tracking/analytics)
    const eventCounts = countRoomEvents(gameState);

    // Accumulate event statistics in raid object for later retrieval
    if (!raid.eventStats) {
      raid.eventStats = { skillCheckEvents: 0, bonusLootEvents: 0 };
    }
    raid.eventStats.skillCheckEvents += eventCounts.skillCheckCount;
    raid.eventStats.bonusLootEvents += eventCounts.hasBonusLoot ? 1 : 0;

    // Cleanup all room events (skill-check, bonus loot, fishing) via eventSystem
    cleanupRoomEvents(gameState);

    debug(CH.raid, `Completing room ${raid.currentRoomIndex}/${gameState.currentRaid.selectedPath.length - 1}: ${room.name} (${room.type})`);

    switch(room.type) {
      case 'exit':
        processExitRoomCompletion(raid, room);
        return false;
      case 'start':
        processEntryRoomCompletion(raid, room);
        break;
      case 'road':
        processRoadRoomCompletion(raid, room);
        break;
      case 'campsite':
        processCampsiteRoomCompletion(raid, room);
        break;
      case 'water':
        processWaterRoomCompletion(raid, room);
        break;
      case 'gate':
      case 'hazard':
        const proceed = processNormalRoomCompletion(raid, room);
        if (!proceed) return false;
    }

    raid.currentRoomIndex++;
    //raid.nextRoomTime = Date.now() + (room.baseDuration*1000);

    // Check if we've completed all rooms or raid failed
    //console.log('checking currentRoomIndex', raid.currentRoomIndex, gameState.currentRaid.selectedPath.length, raid.raidSuccess);
    if (raid.currentRoomIndex >= gameState.currentRaid.selectedPath.length || !raid.raidSuccess) {
        finishRaidCore();
        return false; // Don't enter next area
    }
    else {
        return true; // Continue to next area
    }
}

function processExitRoomCompletion(raid, room) {
  addRaidMessage(`🌌 Reached the exit portal! Raid completed successfully!`, 'success', {
      notificationMessage: `🌌 Raid completed successfully!`,
      stepIndex: raid.currentRoomIndex
  });

  room.result = { success: true, damage: 0, healing: 0, loot: {} };
  raid.currentRoomIndex++;
  finishRaidCore();
}

function processEntryRoomCompletion(raid, room) {
  const nextPathStep = gameState.currentRaid.selectedPath[raid.currentRoomIndex];
  const nextRoom = nextPathStep.room;

  room.result = { success: true, damage: 0, healing: 0, loot: {} };
}

function processRoadRoomCompletion(raid, room) {
  addRaidMessage(`🛣️ Leaving ${room.name}`, 'info', {
      notificationMessage: `🛣️ Leaving ${room.name}`,
      stepIndex: raid.currentRoomIndex
  });

  room.result = { success: true, damage: 0, healing: 0, loot: {} };
}

function processCampsiteRoomCompletion(raid, room) {
  const raidTeam = getRaidTeam();
  const healingAmount = room.healingAmount || 30;

  Object.entries(raid.lootGained).forEach(([item, amount]) => {
      if (item === 'Fish') {
          const foodHealingAmount = room.healingAmount * 0.1 * amount;

          healTeam(raidTeam, foodHealingAmount);
          addRaidMessage(`🐟 Team eats a meal of fresh caught fish`, 'success', {
              notificationMessage: `🐟 Team healed for ${foodHealingAmount} HP`,
              stepIndex: raid.currentRoomIndex
          });
      }
  });

  healTeam(raidTeam, healingAmount);
  addRaidMessage(`💚 Team tended to their wounds`, 'success', {
      notificationMessage: `💚 Team healed for ${healingAmount} HP`,
      stepIndex: raid.currentRoomIndex
  });

  // Show healing details for each team member
  raidTeam.forEach(member => {
      if (member.currentHp > 1) {
          addRaidMessage(`💚 ${member.name} recovered HP (${member.currentHp}/${member.maxHp})`, 'success', {
              notify: false,
              stepIndex: raid.currentRoomIndex
          });
      }
  });

  room.result = { success: true, damage: 0, healing: healingAmount, loot: {} };
  raid.roomsCompleted++;
}

function processWaterRoomCompletion(raid, room) {
  // Fishing events are handled separately
  room.result = { success: true, damage: 0, healing: 0, loot: {} };
  raid.roomsCompleted++;
}

function processNormalRoomCompletion(raid, room) {
  // Check if all skill-check events have completed (event system handles outcomes and damage)
  const tier = gameState.currentRaid.currentRaidGrid.tier;

  const skillCheckEvents = raid.events?.skillCheckEvents || [];
  const allEventsCompleted = skillCheckEvents.length === 0 ||
      skillCheckEvents.every(e => e.status === 'completed' || e.status === 'failed');

  // If events haven't finished yet, don't process room completion
  if (skillCheckEvents.length > 0 && !allEventsCompleted) {
      return false;
  }

  const roomSuccess = !raid.hasSkillEventFailure;
  room.result = { success: roomSuccess};
  if (roomSuccess) {
      addRaidMessage(`✅ Area secured successfully!`, 'success', {
          notificationMessage: `✅ ${room.name} secured!`,
          stepIndex: raid.currentRoomIndex
      });
      raid.roomsCompleted++;
  } else {
      addRaidMessage(`❌ Failed to clear ${room.name}!`, 'failure', { stepIndex: raid.currentRoomIndex });

      // A member who went down stops contributing their skills to the rest of
      // the raid. calculateTeamSkills filters on isAlive, so this recalculation
      // is what actually removes them — without it a knocked-out specialist
      // keeps passing checks for every remaining room.
      //
      // RESTORED 2026-08-02. The old applyDamageToTeam did this inline; the
      // rewrite to per-event damage in eventSystem-core dropped it, and nothing
      // caught it because applyDamageToTeam was left in place as dead code that
      // still looked like the live path. Consequence while it was missing:
      // raids were EASIER than designed, so expect Tier 3 success to fall now.
      raid.teamSkills = calculateTeamSkills(getRaidTeam());

      // Check if raid should continue - only team wipe ends the raid
      if (!hasAliveMember(getRaidTeam())) {
          addRaidMessage(`💀 All team members incapacitated - raid terminated!`, 'failure', {
              notificationMessage: `💀 All team members incapacitated!`,
              stepIndex: raid.currentRoomIndex
          });
          raid.raidSuccess = false;
          finishRaidCore();
          return false;
      } else {
          addRaidMessage(`⚠️ The team continues despite setbacks...`, 'info', { notify: false, stepIndex: raid.currentRoomIndex });
      }
  }

  // Award base room credits
  const tierData = config.raidTierData[tier];
  if (tierData && tierData.creditRewards) {
      let baseCredit = tierData.creditRewards.baseRoomCredit;
      // Apply credit scaling multipliers
      baseCredit = applyCreditsScaling(baseCredit, tier);
      addLoot(raid.lootGained, 'Credits', baseCredit);
      addRaidMessage(`💰 Intelligence report payout: ${baseCredit} credits`, 'bonus', { stepIndex: raid.currentRoomIndex });

      // Award completion bonus if all skill-check events passed
      if (!raid.hasSkillEventFailure) {
          let completionBonus = tierData.creditRewards.completionBonus;
          // Apply credit scaling multipliers
          completionBonus = applyCreditsScaling(completionBonus, tier);
          addLoot(raid.lootGained, 'Credits', completionBonus);
          addRaidMessage(`💰 Complete intelligence bonus: ${completionBonus} credits`, 'bonus', { stepIndex: raid.currentRoomIndex });
      }
  }

  addRaidMessage(`📍 Leaving ${room.name} (${raid.currentRoomIndex}/${gameState.currentRaid.selectedPath.length - 2})`, 'info', {
      notificationMessage: `📍 Leaving ${room.name}`,
      stepIndex: raid.currentRoomIndex
  });
  
  return true;
}

export function enterNextRoom() {
    const raid = gameState.currentRaid.raidInProgress;
    const pathStep = gameState.currentRaid.selectedPath[raid.currentRoomIndex];
    const room = pathStep.room;

    // Schedule room start and end times
    raid.roomStartTime = Date.now();
    raid.nextRoomTime = raid.roomStartTime + room.baseDuration * 1000;

    switch (room.type) {
      case 'gate':
      case 'hazard':
        addRaidMessage(`📍 Entered ${room.name} (${raid.roomsCompleted+1}/${countNonRoadSteps(gameState.currentRaid.selectedPath)})`, 'info', {
            notificationMessage: `📍 Entered ${room.name}`,
            stepIndex: raid.currentRoomIndex
        });
        // Initialize skill-check events and conditional bonus loot via eventSystem
        initializeRoomEvents(room, gameState);
        break;
      case 'road':
        addRaidMessage(`🛣️ Entered ${room.name}`, 'info', {
            notificationMessage: `🛣️ Entered ${room.name}`,
            stepIndex: raid.currentRoomIndex
        });
        break;
      case 'campsite':
        addRaidMessage(`🏕️ Team sets up camp and recovers`, 'info', {
            notificationMessage: `🏕️ Found a safe campsite`,
            stepIndex: raid.currentRoomIndex
        });
        break;
      case 'water':
        addRaidMessage(`🌊 Team discovers a fishing spot`, 'info', {
            notificationMessage: `🌊 Found water source`,
            stepIndex: raid.currentRoomIndex
        });
        // Initialize fishing event via eventSystem
        initializeRoomEvents(room, gameState);
        break;
    }    
}

export async function finishRaidCore() {
  const raid = gameState.currentRaid.raidInProgress;
  const region = gameState.currentRaid.currentRaidGrid.region;

  // Clean up any active special events
  //if (raid.specialEvent && raid.specialEvent.active) {
      //cleanupBonusEvent();
  //}

  // Calculate final results
  const tier = gameState.currentRaid.currentRaidGrid.tier;
  const finalResults = calculateRaidResults(raid.raidSuccess, raid.roomsCompleted, gameState.currentRaid.selectedPath.length, region, getRaidTeam(), tier);

  // Calculate contractor payments from total credits earned
  const totalContractorPercentage = gameState.currentRaid.hiredContractors.length > 0 ? gameState.currentRaid.hiredContractors.reduce((sum, contractor) => sum + contractor.rewardPercentage, 0) : 0;
  const contractorPayment = Math.floor(finalResults.creditsEarned * totalContractorPercentage / 100);
  const playerCredits = finalResults.creditsEarned - contractorPayment;

  // Note: Credit scaling is already applied when individual credits are looted (base room, events, completion bonus)
  // This finalResults amount is a bonus calculation separate from looted credits
  // Apply results to hiring player (they pay for all contractors)
  gameState.player.credits += playerCredits;

  // Calculate XP for contractors (before payments to avoid duplication)
  const xpEarned = calculateRaidXP(raid.raidSuccess, raid.roomsCompleted, gameState.currentRaid.selectedPath.length, region, getRaidTeam(), tier);
  const contractorXP = Math.floor(xpEarned * 0.8); // Contractors get 80% of player XP

  // Calculate individual contractor payments and send to owners
  const contractorPayments = gameState.currentRaid.hiredContractors.map(contractor => ({
      contractorId: contractor.id,
      contractorName: contractor.name,
      isPlayerContractor: contractor.id > 0,
      percentage: contractor.rewardPercentage,
      credits: Math.floor(finalResults.creditsEarned * contractor.rewardPercentage / 100),
      xp: contractorXP
  }));

  // Send credits and XP to player contractor owners (AI contractors don't get real credits/XP).
  // Awaited so the payments are recorded while the contractors are still marked
  // hired — the server verifies the hire relationship, and the release below
  // clears it (see finishRaid contractor release).
  const onlinePayments = contractorPayments.filter(p => p.isPlayerContractor);
  if (onlinePayments.length > 0) {
      await recordContractorPayments(onlinePayments);
  }
  if (finalResults.creditsEarned > 0) {
      addRaidMessage(`💰 Total raid earnings: ${finalResults.creditsEarned} credits`, 'success', {
          stepIndex: gameState.currentRaid.selectedPath.length-1,
          notificationMessage: `💰 Earned ${playerCredits} credits`
      });
      if (contractorPayment > 0) {
          addRaidMessage(`💼 Contractor payments: ${contractorPayment} credits (${totalContractorPercentage}%)`, 'info', { notify: false, stepIndex: gameState.currentRaid.selectedPath.length-1});
          addRaidMessage(`💰 Your share: ${playerCredits} credits`, 'success', { notify: false, stepIndex: gameState.currentRaid.selectedPath.length-1});
      }
  }

  // Award XP for the raid (already calculated above for contractor payments)
  if (xpEarned > 0) {
      addRaidMessage(`⭐ Gained ${xpEarned} experience points`, 'success', {
          stepIndex: gameState.currentRaid.selectedPath.length-1,
          notificationMessage: `⭐ Gained ${xpEarned} XP`
      });
      gainExperienceCore(gameState.character, xpEarned);

      // Award XP to all contractors who participated in the raid
      if (gameState.currentRaid.hiredContractors && gameState.currentRaid.hiredContractors.length > 0) {
          gameState.currentRaid.hiredContractors.forEach(contractor => {
              if (contractor) {
                  gainContractorExperienceCore(contractor, contractorXP);
              }
          });

          if (contractorXP > 0) {
              addRaidMessage(`👥 Contractors gained ${contractorXP} experience points`, 'info', {
                  stepIndex: gameState.currentRaid.selectedPath.length-1,
                  notify: false
              });
          }
      }
  }

  // Add loot to inventory and credits (per-item scarcity is applied at drop
  // time via weighted selection, so logged loot matches inventory exactly)
  if (raid.lootGained) {
      gameState.character.inventory = gameState.character.inventory || {};
      Object.entries(raid.lootGained).forEach(([item, amount]) => {
          if (item === 'Credits' && amount) {
              gameState.player.credits += amount;
          } else if (raid.raidSuccess && item != 'Fish') {
              gameState.character.inventory[item] = (gameState.character.inventory[item] || 0) + amount;
          }
      });
  }

  // Apply injuries to contractors (they heal over time)
  applyPostRaidInjuries(getRaidTeam());

  // Final status report
  if (raid.raidSuccess) {
      addRaidMessage(`🎉 Raid completed successfully!`, 'success', {stepIndex: gameState.currentRaid.selectedPath.length-1});
      // Lifetime counter for the Command Marks formula
      if (gameState.currentRaid.currentRaidGrid?.tier === 3 && gameState.character) {
          ensureLifetimeCounters(gameState.character);
          gameState.character.lifetime.t3Wins += 1;
      }

      // Collapse Key recovery. The whole of the story track's award path is
      // this call: keys are account-level records rather than loot, so nothing
      // here touches lootGained, the inventory, or any drop table.
      if (gameState.character) {
          const key = rollCollapseKeyCore(
              gameState.character.faction,
              gameState.currentRaid.currentRaidGrid?.tier || 1
          );
          if (key) {
              addRaidMessage(`🗝️ Collapse Key ${key.code} recovered — “${key.name}”. Filed to the Archive.`, 'bonus', {
                  stepIndex: gameState.currentRaid.selectedPath.length - 1,
                  notificationMessage: `🗝️ Collapse Key ${key.code} — “${key.name}”`
              });
          }
      }
  } else {
      addRaidMessage(`💀 Raid failed - retreat initiated!`, 'failure', {stepIndex: gameState.currentRaid.selectedPath.length-1});
      addRaidMessage(`💀 Raid failed - items dropped!`, 'failure', {stepIndex: gameState.currentRaid.selectedPath.length-1});
  }

  addRaidMessage(`📊 Final Report: ${raid.roomsCompleted}/${countNonRoadSteps(gameState.currentRaid.selectedPath)} rooms cleared without incident`, 'info', { notify: false, stepIndex: gameState.currentRaid.selectedPath.length-1 });

  // Save completed raid info for restoration after page reload
  gameState.currentRaid.completedRaidInfo = {
      region: region,
      selectedPath: [...gameState.currentRaid.selectedPath],
      roomsCompleted: raid.roomsCompleted,
      raidSuccess: raid.raidSuccess,
      logEntries: raid.logEntries ? [...raid.logEntries] : [],
      stepLogs: raid.stepLogs ? JSON.parse(JSON.stringify(raid.stepLogs)) : {},
      currentRaidGrid: gameState.currentRaid.currentRaidGrid,
      eventStats: raid.eventStats,
      // Snapshot for the raid-panel Loot tab (raidInProgress is torn down
      // after this; on failure the items were discarded — the tab shows them
      // as lost)
      lootGained: raid.lootGained ? { ...raid.lootGained } : {}
  };

  // Settle the Blackout contract. Only a SUCCESSFUL run pays — the modifiers
  // are the price of the perk, and clearing on any completion would let a
  // player arm the hardest contract and walk into the first hazard.
  const blackoutEarned = clearActiveBlackout({ success: !!raid.raidSuccess });
  if (blackoutEarned) {
      gameState.currentRaid.completedRaidInfo.blackoutEarned = blackoutEarned;
      addNotification(
          `🌑 ${getBlackoutContract(blackoutEarned.contractId)?.name} cleared — ${blackoutEarned.perk.label}, permanently.`,
          'success',
          { sticky: true } // one-time, irreversible, and easy to miss in the results wall
      );
  }

  // Release hired contractors in database
  const currentCharacter = gameState.characters?.find(c => c.id === gameState.activeCharacterId);
  if (currentCharacter) {
      for (const contractor of gameState.currentRaid.hiredContractors) {
          if (contractor.multiplayerId) {
              await multiplayer.releaseHiredContractor(contractor.multiplayerId, currentCharacter.multiplayerId);
          }
      }
  }

  // Clean up raid state
  gameState.currentRaid.raidInProgress = null;
  gameState.currentRaid.raidCompleted = true;
  gameState.currentRaid.raidOwnerId = null;

  // Succession Doctrine: a raid ending is when the marks rate actually moves,
  // so it is where the promotion peak is re-evaluated. Tracked unconditionally,
  // not only when the charter is owned — the Command panel shows the same rate
  // line, and the spec requires panel and automation to share ONE computation.
  noteRaidCompletedForSuccession();

  addRaidMessage(`🏪 New contractors have arrived at the outpost!`, 'info', {
      notify: false,
      stepIndex: gameState.currentRaid.selectedPath.length-1
  });
}

export function resetRaidCore() {
  // Clean up any active special events
  //if (gameState.currentRaid.raidInProgress && gameState.currentRaid.raidInProgress.specialEvent && gameState.currentRaid.raidInProgress.specialEvent.active) {
      //cleanupBonusEvent();
  //}

  // Clear raid state. selectedRegion/selectedTier deliberately survive the
  // reset so the next New Raid screen starts from the previous choices
  // (players mostly re-run the same setup); generateRegionTiles() still
  // clears a selection that a region lock has made unavailable.
  gameState.currentRaid.currentRaidGrid = null;
  gameState.currentRaid.selectedPath = [];
  gameState.currentRaid.raidCompleted = false;
  gameState.currentRaid.completedRaidInfo = null;
  gameState.currentRaid.raidOwnerId = null;

  // Release marketplace contractors on server before clearing
  const currentCharacter = gameState.characters?.find(c => c.id === gameState.activeCharacterId);
  if (currentCharacter) {
      // Release all reserved marketplace contractors
      gameState.currentRaid.reservedContractors.forEach(contractor => {
          if (contractor.id > 0) { // Marketplace contractor
              multiplayer.releaseContractor(contractor.id, currentCharacter.multiplayerId)
                  .catch(err => console.error('Error releasing reserved contractor:', err));
          }
      });

      // Hired contractors are already released in finishRaid(), no need to release again
  }

  // Clear local contractor arrays
  gameState.currentRaid.reservedContractors = [];
  gameState.currentRaid.hiredContractors = [];
}